/**
 * Settings Service for Web
 * Manages app settings using localStorage
 */

import { v4 as uuidv4 } from 'uuid';
import { AppSettings } from '../types';

const SETTINGS_KEY = 'app_settings';

class SettingsService {
  private async generateGuid(): Promise<string> {
    return uuidv4();
  }

  async getSettings(): Promise<AppSettings> {
    try {
      const data = localStorage.getItem(SETTINGS_KEY);
      
      if (data) {
        return JSON.parse(data);
      }
      
      // Initialize default settings with new GUID
      const userId = await this.generateGuid();
      const defaultSettings: AppSettings = {
        userId,
        username: '',
        syncEnabled: false,
        locationEnabled: true,
      };
      
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(defaultSettings));
      
      return defaultSettings;
    } catch (error) {
      throw error;
    }
  }

  async getUserId(): Promise<string> {
    const settings = await this.getSettings();
    return settings.userId;
  }

  async setUsername(username: string): Promise<void> {
    try {
      const settings = await this.getSettings();
      settings.username = username;
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    } catch (error) {
      throw error;
    }
  }

  async setSyncEnabled(enabled: boolean): Promise<void> {
    try {
      const settings = await this.getSettings();
      settings.syncEnabled = enabled;
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    } catch (error) {
      throw error;
    }
  }

  async isSyncEnabled(): Promise<boolean> {
    try {
      const settings = await this.getSettings();
      return settings.syncEnabled;
    } catch (error) {
      return false;
    }
  }

  async isLocationEnabled(): Promise<boolean> {
    try {
      const settings = await this.getSettings();
      return settings.locationEnabled ?? true;
    } catch (error) {
      return true;
    }
  }

  async setLocationEnabled(enabled: boolean): Promise<void> {
    try {
      const settings = await this.getSettings();
      settings.locationEnabled = enabled;
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    } catch (error) {
      throw error;
    }
  }
}

export const settingsService = new SettingsService();
